/* Admin reference data. In the shipped app this is org-scoped rows the office
   maintains under Settings; here it is the fixture set the design canvas ran on.
   The job form is unusable until this exists, which is why settings gets built
   early (IDEA.md §8). */
import type { ComponentType, EdgeProfile, Sheen, Substrate } from '@/types';

/* The colours are not here any more: they are rows on the server, they arrive
   with the session, and Settings → Colours writes them back through
   /api/colours. Every chip and every picker reads them from `colours` in the
   store, through `colourOf` and `liveColours`. */

/** 2-pac PU sheens, matt through gloss. The index is what a job stores. */
export const SHEENS: Sheen[] = ['Matt', 'Satin', 'Semi-gloss', 'Gloss'];

/** Gloss wants a third coat; everything else takes two. */
export const coatsFor = (sheen: number) => (sheen > 2 ? 3 : 2);

/* The board's stages are not here any more: they are rows on the server, they
   arrive with the session, and Settings → Board stages writes them back through
   /api/stages. Every screen reads them from `stages` in the store. */

/** The tints a stage column can take. "No tint" leaves the column on the ground
 *  colour — the stage name is always shown as well, never colour alone. */
export const TONES = [
  { name: 'No tint', hex: '' },
  { name: 'Slate', hex: '#5D6570' },
  { name: 'Steel', hex: '#4A6B6E' },
  { name: 'Teal', hex: '#1F5F5B' },
  { name: 'Deep teal', hex: '#123B38' },
  { name: 'Sand', hex: '#8A7A55' },
  { name: 'Clay', hex: '#A65A35' },
  { name: 'Olive', hex: '#5B6B45' },
  { name: 'Plum', hex: '#6B5570' },
  { name: 'Graphite', hex: '#3F4A52' },
];

export const TONE_NAME: Record<string, string> = Object.fromEntries(
  TONES.map((t) => [t.hex, t.name]),
);

export const COMPONENT_TYPES: ComponentType[] = [
  'Door',
  'Drawer front',
  'Panel',
  'End panel',
  'Kick',
  'Filler',
  'Shelf',
  'Gable',
];

export const SUBSTRATES: Substrate[] = ['MR MDF', 'Standard MDF', 'Ply', 'Solid timber'];

export const EDGE_PROFILES: EdgeProfile[] = ['Square', 'Shaker', 'V-groove', 'Pencil round'];

/** The searchable pickers the item rows open, over the reference data above. */
export const PICKERS: Record<string, { title: string; hint: string; opts: string[] }> = {
  type: {
    title: 'Component type',
    hint: 'From admin reference data.',
    opts: COMPONENT_TYPES,
  },
  substrate: {
    title: 'Substrate',
    hint: 'What the component is made of.',
    opts: SUBSTRATES,
  },
  edge: {
    title: 'Edge profile',
    hint: 'Profile sprayed on the visible edge.',
    opts: EDGE_PROFILES,
  },
  finish: {
    title: 'Finish',
    hint: '2-pac PU. Coat count pre-fills from the finish.',
    opts: SHEENS,
  },
};

export const RANGES = [
  { label: 'Week', days: 7 },
  { label: 'Fortnight', days: 14 },
  { label: 'Month', days: 28 },
  { label: '3 months', days: 91 },
];

/* The shop's own details are not here any more: they are real columns on the
   organisation, they arrive with the session, and Settings → Organisation
   writes them back through /api/organisation. */
